import { map, switchMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { PlatformContextService } from '@core/services/platform-context.service';
import { updateItemInArrayByIndex } from '@core/utils/array.utils';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';

import { of } from 'rxjs';
import {
  ACTIVE_PLATFORMS,
  PlatformAccessStatus,
  PlatformActiveStatus,
  PlatformConfig,
  Platforms,
  PlatformsBackend
} from '@core/models/platforms.model';
import {
  ContextStatus,
  IAllPlatformContextsResponse,
  IPlatformContext,
  PlatformContextMappedType
} from '@core/models/platform-context.model';
import { GetPlatformContexts, GetPlatformsStatus, UpdatePlatformStatus } from '@core/actions';
import { AynService } from '@core/services/ayn.service';
import { UserState } from '@core/state/user.state';

export namespace PlatformContext {
  export interface State {
    contexts: IPlatformContext[];
    platformsStatus: PlatformActiveStatus;
  }
}

export const PLATFORM_CONTEXTS_DEFAULT: PlatformContext.State = {
  contexts: [],
  platformsStatus: {
    [PlatformsBackend.Facebook]: PlatformAccessStatus.ENABLE,
    [PlatformsBackend.Google]: PlatformAccessStatus.ENABLE,
    [PlatformsBackend.LinkedIn]: PlatformAccessStatus.ENABLE,
    [PlatformsBackend.Bing]: PlatformAccessStatus.ENABLE,
    [PlatformsBackend.TikTok]: PlatformAccessStatus.ENABLE,
    [PlatformsBackend.AdCreative]: PlatformAccessStatus.ENABLE
  }
};

function isPlatformContextMissing(platformContextRes: IAllPlatformContextsResponse): boolean {
  return ACTIVE_PLATFORMS.some((activePlatform) =>
    platformContextRes.every((context) => context.source !== activePlatform.value)
  );
}

@State<PlatformContext.State>({
  name: 'PlatformContextState',
  defaults: PLATFORM_CONTEXTS_DEFAULT
})
@Injectable()
export class PlatformContextState {
  constructor(
    private platformContextService: PlatformContextService,
    private aynService: AynService,
    private store: Store
  ) {}

  @Selector()
  static PlatformContextsMapped({ contexts }: PlatformContext.State): PlatformContextMappedType {
    return contexts
      .filter((c) => PlatformConfig.some((pc) => pc.value === c.source && pc.isActive))
      .reduce((platformContext, cur: IPlatformContext) => {
        return {
          ...platformContext,
          [Platforms[cur.source]]: cur
        };
      }, {} as PlatformContextMappedType);
  }

  @Selector()
  static PlatformsStatus({ platformsStatus }: PlatformContext.State): PlatformActiveStatus {
    return platformsStatus;
  }

  @Selector()
  static PlatformContexts({ contexts }: PlatformContext.State) {
    return contexts;
  }

  @Selector()
  static EnabledPlatformContexts({ platformsStatus, contexts }: PlatformContext.State) {
    return contexts.filter(
      (context) =>
        platformsStatus[context.source] === PlatformAccessStatus.ENABLE && context.status === ContextStatus.Connected
    );
  }

  static getPlatformContext(platform: Platforms) {
    return createSelector([PlatformContextState], ({ contexts }: PlatformContext.State) =>
      contexts.find((o) => o.source === platform)
    );
  }

  @Selector()
  static ConnectedPlatformContexts({ contexts }: PlatformContext.State) {
    return contexts.filter((o) => o.status === ContextStatus.Connected);
  }

  @Selector()
  static RequireReconnectionPlatforms({ contexts }: PlatformContext.State) {
    return contexts.filter((o) => o.status == ContextStatus.RequireReConnect);
  }

  @Action(GetPlatformContexts)
  get({ patchState }: StateContext<PlatformContext.State>) {
    const isSubAccount = this.store.selectSnapshot(UserState.isInviterUser);

    const getPlatformContexts = (
      isSubAccount ? this.platformContextService.getOwner$() : this.platformContextService.get$()
    ).pipe(map((res) => res.data));

    const createUnownedPlatformContexts$ = this.platformContextService
      .createUnownedPlatformContexts$()
      .pipe(switchMap(() => getPlatformContexts));

    return getPlatformContexts.pipe(
      switchMap((data) => (isPlatformContextMissing(data) ? createUnownedPlatformContexts$ : of(data))),
      tap((contexts) => patchState({ contexts }))
    );
  }

  @Action(UpdatePlatformStatus)
  updatePlatformStatus(
    { patchState, getState }: StateContext<PlatformContext.State>,
    { payload }: UpdatePlatformStatus
  ) {
    const { contexts } = getState();
    const platformIndex = contexts.findIndex((o) => o.source === payload.source);

    if (platformIndex > -1) {
      const newContexts = updateItemInArrayByIndex(contexts, platformIndex, (v) => ({ ...v, status: payload.status }));

      patchState({
        contexts: newContexts
      });
    }
  }

  @Action(GetPlatformsStatus)
  getPlatformsStatus({ patchState }: StateContext<PlatformContext.State>) {
    return this.aynService.getPlatformsStatus$().pipe(
      tap((platformsStatus) => {
        patchState({
          platformsStatus: {
            [PlatformsBackend.Facebook]: platformsStatus.facebook,
            [PlatformsBackend.Google]: platformsStatus.google,
            [PlatformsBackend.LinkedIn]: platformsStatus.linkedin,
            [PlatformsBackend.Bing]: platformsStatus.bing,
            [PlatformsBackend.TikTok]: platformsStatus.tiktok,
            [PlatformsBackend.AdCreative]: platformsStatus.adcreative
          }
        });
      })
    );
  }
}
